/**
 * 全局状态管理 (Zustand)
 */

import { create } from "zustand";
import type { PasswordItem, PasswordCategory, DecryptedItem } from "@passkeeper/shared";
import { getItems, setToken as saveToken, getToken } from "./api";
import { decrypt } from "@passkeeper/shared";

interface AppState {
  // 认证
  isLoggedIn: boolean;
  userId: string | null;
  email: string | null;
  masterPassword: string | null; // 内存中暂存，永不持久化

  // 密码条目
  items: PasswordItem[];
  decryptedItems: DecryptedItem[];
  selectedCategory: PasswordCategory | "all";
  searchQuery: string;
  isLoading: boolean;

  // 当前查看/编辑的条目
  editingItem: DecryptedItem | null;
  viewingItem: DecryptedItem | null;

  // 动作
  login: (userId: string, email: string, token: string, masterPassword: string) => void;
  logout: () => void;
  setMasterPassword: (pw: string) => void;
  setItems: (items: PasswordItem[]) => void;
  setSelectedCategory: (cat: PasswordCategory | "all") => void;
  setSearchQuery: (q: string) => void;
  setIsLoading: (v: boolean) => void;
  setEditingItem: (item: DecryptedItem | null) => void;
  setViewingItem: (item: DecryptedItem | null) => void;

  // 解密所有条目（本地执行）
  decryptItems: () => Promise<void>;
  encryptItem: (plaintext: string) => Promise<{ encData: string; salt: string; iv: string }>;
}

export const useStore = create<AppState>((set, get) => ({
  isLoggedIn: !!getToken(),
  userId: null,
  email: null,
  masterPassword: null,
  items: [],
  decryptedItems: [],
  selectedCategory: "all",
  searchQuery: "",
  isLoading: false,
  editingItem: null,
  viewingItem: null,

  login: (userId, email, token, masterPassword) => {
    saveToken(token);
    set({ isLoggedIn: true, userId, email, masterPassword });
  },

  logout: () => {
    saveToken(null);
    set({
      isLoggedIn: false,
      userId: null,
      email: null,
      masterPassword: null,
      items: [],
      decryptedItems: [],
      viewingItem: null,
      editingItem: null,
    });
  },

  setMasterPassword: (pw) => set({ masterPassword: pw }),
  setItems: (items) => set({ items }),
  setSelectedCategory: (cat) => set({ selectedCategory: cat }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setIsLoading: (v) => set({ isLoading: v }),
  setEditingItem: (item) => set({ editingItem: item }),
  setViewingItem: (item) => set({ viewingItem: item }),

  decryptItems: async () => {
    const { items, masterPassword } = get();
    if (!masterPassword) {
      // 需要主密码
      return;
    }

    const decrypted: DecryptedItem[] = [];
    for (const item of items) {
      try {
        const plaintext = item.encData
          ? await decrypt(masterPassword, item.encData, item.iv, item.salt)
          : JSON.stringify({
              username: item.username,
              password: item.password,
              notes: item.notes,
            });

        let parsed: { username?: string; password?: string; notes?: string };
        try {
          parsed = JSON.parse(plaintext);
        } catch {
          parsed = { username: plaintext, password: "", notes: "" };
        }

        decrypted.push({
          id: item.id,
          title: item.title,
          url: item.url,
          domain: item.domain,
          username: parsed.username || item.username,
          password: parsed.password || item.password,
          notes: parsed.notes || item.notes,
          category: item.category,
          favicon: item.favicon,
          strength: item.strength,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        });
      } catch {
        // 解密失败，可能主密码不正确
        decrypted.push({
          id: item.id,
          title: item.title,
          url: item.url,
          domain: item.domain,
          username: "🔒 需要解密",
          password: "🔒 需要解密",
          notes: "🔒 需要解密",
          category: item.category,
          favicon: item.favicon,
          strength: item.strength,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        });
      }
    }
    set({ decryptedItems: decrypted });
  },

  encryptItem: async (plaintext: string) => {
    const { masterPassword } = get();
    if (!masterPassword) throw new Error("未设置主密码");
    const { cipher, iv, salt } = await import("@passkeeper/shared").then((m) =>
      m.encrypt(masterPassword, plaintext)
    );
    return { encData: cipher, salt, iv };
  },
}));